/* eslint-disable no-constant-condition, no-use-before-define */
import * as Node from './Node';
import TOKEN from './TOKEN';
import tokenize from './tokenize';

/**
 * Parses a chat message into a Message node.
 * Input parameters can either be the result returned from `tokenize()` or the input parameters for `tokenize()`.
 */
export default (...args) => {
  const tokens = typeof args[0] === 'string' ? tokenize(...args) : args[0];
  let current = tokens.next();

  /*
    Message       ::= SlashShrug? Segment* Shrug?
    SlashShrug    ::= SLASH_SHRUG | e
    Shrug         ::= SHRUG | e
    Segment       ::= EMOJI | SHORTCODE | EMOTICON | CODE | List | MENTION | NEWLINE | URL | TEXT | e
    List          ::= ListItem<ORDERED_LIST>* | ListItem<UNORDERED_LIST>*
    ListItem      ::= ORDERED_LIST Segment* | UNORDERED_LIST Segment*
  */

  const terminal = (type) => {
    const node = current;
    current = tokens.next();
    return node;
  };

  function Message() {
    const slashShrug = SlashShrug();
    const segments = [];
    while (true) {
      const segment = Segment();
      if (segment) segments.push(segment);
      else break;
    }
    const shrug = Shrug();
    return new Node.Message(slashShrug, segments, shrug);
  }

  function SlashShrug() {
    switch (current.type) {
      case TOKEN.SLASH_SHRUG:
        return new Node.SlashShrug(terminal());
      default:
        return undefined;
    }
  }

  function Shrug() {
    switch (current.type) {
      case TOKEN.SHRUG:
        return new Node.Shrug(terminal());
      default:
        return undefined;
    }
  }

  function Segment() {
    switch (current.type) {
      case TOKEN.EMOJI:
        return new Node.Emoji(terminal());
      case TOKEN.SHORTCODE: {
        const token = terminal();
        // NOTE: shortcodes can return false positives.  If it is invalid, then return it as text.
        return token.emoji ? new Node.Shortcode(token) : new Node.Text({ ...token, type: TOKEN.TEXT });
      }
      case TOKEN.EMOTICON:
        return new Node.Emoticon(terminal());
      case TOKEN.TEXT:
        return new Node.Text(terminal());
      case TOKEN.CODE:
        return new Node.Code(terminal());
      case TOKEN.MENTION:
        return new Node.Mention(terminal());
      case TOKEN.NEWLINE:
        return new Node.Newline(terminal());
      case TOKEN.URL:
        return new Node.URL(terminal());
      case TOKEN.ORDERED_ITEM:
      case TOKEN.UNORDERED_ITEM:
        return List();
      default:
        return undefined;
    }
  }

  function List() {
    const { type } = current;
    const items = [];
    while (true) {
      if (current.type === type) {
        const item = ListItem();
        items.push(item);
      } else break;
    }
    return new Node.List(items);
  }

  function ListItem() {
    const bullet = terminal();
    const segments = [];
    while (true) {
      if (current.type === TOKEN.EOF) break;
      const segment = Segment();
      if (segment) segments.push(segment);
      if (!segment || segment instanceof Node.Newline) break;
    }
    return new Node.ListItem(bullet, segments);
  }

  const message = Message();
  message.finalize();
  return message;
};
